/**
 * PatrolFlow - Lazy Loading Helper with Retry Logic
 * 
 * Copyright (c) 2025 PatrolFlow. All rights reserved.
 * 
 * Helper for lazy loading with retry logic for reliable chunk loading
 */

import { lazy, ComponentType } from 'react';

export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      // Retry once if chunk fails to load (common with code splitting)
      console.warn('Failed to load chunk, retrying...', error);
      return componentImport();
    }
  });
}

// Enhanced loader with loading state management
export function createLazyLoader<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  componentName: string
) {
  return lazy(async () => {
    console.log(`⚡ Loading ${componentName} chunk...`);
    const start = performance.now();
    
    try {
      const module = await componentImport();
      const loadTime = performance.now() - start;
      console.log(`✅ ${componentName} loaded in ${loadTime.toFixed(1)}ms`);
      return module;
    } catch (error) {
      console.warn(`⚠️ Failed to load ${componentName}, retrying...`, error);
      return componentImport();
    }
  });
}